import {
  faArrowDownLeft,
  faArrowDownRight,
  faArrowsUpDownLeftRight,
  faArrowUpLeft,
  faArrowUpRight,
  IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  MenuPlacement,
  MenuToggleButton,
  MenuToggleButtonProps,
  Text,
} from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { TFunction } from "i18next";
import { ThumbnailPosition } from "../video-sdk";
import { SelfViewControlButton } from "./SelfViewControlButton";
import { MENU_Z_INDEX_CLASSNAME } from "./constants";

interface PositionAttributes {
  placement: MenuPlacement;
  icon: IconDefinition;
  label: string;
  srLabel: string;
}

function getPositionAttributesByPosition(
  t: TFunction,
): Record<ThumbnailPosition, PositionAttributes> {
  return {
    [ThumbnailPosition.TOP_RIGHT]: {
      placement: "bottom end",
      icon: faArrowUpRight,
      label: t("telehealth-core:video.topRightCorner"),
      srLabel: t("telehealth-core:video.moveVideoToTopRightCorner"),
    },
    [ThumbnailPosition.TOP_LEFT]: {
      placement: "bottom end",
      icon: faArrowUpLeft,
      label: t("telehealth-core:video.topLeftCorner"),
      srLabel: t("telehealth-core:video.moveVideoToTopLeftCorner"),
    },
    [ThumbnailPosition.BOTTOM_LEFT]: {
      placement: "top end",
      icon: faArrowDownLeft,
      label: t("telehealth-core:video.bottomLeftCorner"),
      srLabel: t("telehealth-core:video.moveVideoToBottomLeftCorner"),
    },
    [ThumbnailPosition.BOTTOM_RIGHT]: {
      placement: "top end",
      icon: faArrowDownRight,
      label: t("telehealth-core:video.bottomRightCorner"),
      srLabel: t("telehealth-core:video.moveVideoToBottomRightCorner"),
    },
  };
}

export function SetPositionMenu({
  position,
  setPosition,
  isMobile,
}: {
  position: ThumbnailPosition;
  setPosition: (thumbnailPosition: ThumbnailPosition) => void;
  isMobile: boolean;
}) {
  const t = useTranslate();
  const positionAttributesByPosition = getPositionAttributesByPosition(t);
  const currentPosition = positionAttributesByPosition[position];
  const positionChoices = Object.entries(positionAttributesByPosition).filter(
    ([thumbnailPosition, _]) => thumbnailPosition != position,
  );

  return (
    <Menu
      className="absolute bottom-2 right-2"
      menuClassName={MENU_Z_INDEX_CLASSNAME}
      placement={currentPosition.placement}
      toggleButton={
        <SelfViewControlButton<MenuToggleButtonProps>
          as={MenuToggleButton}
          buttonClassName="group/move-button"
          icon={faArrowsUpDownLeftRight}
          label={t("telehealth-core:video.moveVideo")}
          labelClassName="group-hover/move-button:block group-focus/move-button:block"
          labelIsVisible={!isMobile}
          screenReaderLabel={t("telehealth-core:video.changeVideoPosition")}
          use="unstyled"
          visibleCaret={false}
        />
      }
    >
      <Text as="span" className="px-4 pt-1" variant="sm" aria-hidden>
        {t("telehealth-core:video.moveVideo")}
      </Text>
      <MenuDivider />
      <>
        {positionChoices.map(([thumbnailPosition, positionAttributes]) => (
          <MenuItem
            key={thumbnailPosition}
            onClick={() => setPosition(thumbnailPosition as ThumbnailPosition)}
            className="flex items-center gap-2"
          >
            <Icon
              aria-hidden
              icon={positionAttributes.icon}
              className="w-4 h-4"
            />
            <span className="sr-only">{positionAttributes.srLabel}</span>
            <span aria-hidden>{positionAttributes.label}</span>
          </MenuItem>
        ))}
      </>
    </Menu>
  );
}
